/**
 * useBulkGoalActions — Bulk archive / restore / hard-delete operations on goals.
 * Uses a soft-delete (archived_at) so users can recover for 30 days.
 */
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQueryInvalidation } from "@/hooks/queries/useQueryInvalidation";
import { logger } from "@/utils/logger";

export const useBulkGoalActions = () => {
  const { user } = useAuth();
  const { invalidateGoals } = useQueryInvalidation();

  const archiveGoals = useCallback(
    async (goalIds: string[]) => {
      if (!user?.id || goalIds.length === 0) return 0;
      const { error, count } = await supabase
        .from("goals")
        .update({ archived_at: new Date().toISOString() }, { count: "exact" })
        .in("id", goalIds)
        .eq("user_id", user.id);

      if (error) {
        logger.error("[useBulkGoalActions] archiveGoals error:", error);
        throw error;
      }
      await invalidateGoals();
      return count ?? goalIds.length;
    },
    [user?.id, invalidateGoals]
  );

  const restoreGoals = useCallback(
    async (goalIds: string[]) => {
      if (!user?.id || goalIds.length === 0) return 0;
      const { error, count } = await supabase
        .from("goals")
        .update({ archived_at: null }, { count: "exact" })
        .in("id", goalIds)
        .eq("user_id", user.id);

      if (error) {
        logger.error("[useBulkGoalActions] restoreGoals error:", error);
        throw error;
      }
      await invalidateGoals();
      return count ?? goalIds.length;
    },
    [user?.id, invalidateGoals]
  );

  /**
   * Permanently delete many goals in a single bulk operation.
   * Preserves certificates (independent table) and unlinks reward pledges
   * before removing the goals so reward history stays intact.
   */
  const bulkDeleteGoals = useCallback(
    async (goalIds: string[]) => {
      if (!user?.id || goalIds.length === 0) return 0;

      // 1. Look up community-goal links for cleanup of participation rows.
      const { data: goalRows, error: fetchGoalsError } = await supabase
        .from("goals")
        .select("id, common_goal_id")
        .in("id", goalIds)
        .eq("user_id", user.id);
      if (fetchGoalsError) {
        logger.error("[useBulkGoalActions] bulkDelete fetch goals error:", fetchGoalsError);
        throw fetchGoalsError;
      }

      const safeGoalIds = (goalRows ?? []).map((g) => g.id);
      if (safeGoalIds.length === 0) return 0;

      const commonGoalIds = Array.from(
        new Set((goalRows ?? []).map((g) => g.common_goal_id).filter((v): v is string => !!v))
      );

      // 2. Collect todo ids in one query so we can unlink pledges in bulk.
      const { data: todoRows, error: fetchTodosError } = await supabase
        .from("todos")
        .select("id")
        .in("goal_id", safeGoalIds);
      if (fetchTodosError) {
        logger.error("[useBulkGoalActions] bulkDelete fetch todos error:", fetchTodosError);
        throw fetchTodosError;
      }
      const todoIds = (todoRows ?? []).map((t) => t.id);

      // 3. Unlink reward pledges so reward history is preserved.
      const { error: unlinkGoalPledgesError } = await supabase
        .from("reward_pledges")
        .update({ goal_id: null })
        .in("goal_id", safeGoalIds);
      if (unlinkGoalPledgesError) {
        logger.error("[useBulkGoalActions] unlink goal pledges error:", unlinkGoalPledgesError);
        throw unlinkGoalPledgesError;
      }

      if (todoIds.length > 0) {
        const { error: unlinkTodoPledgesError } = await supabase
          .from("reward_pledges")
          .update({ todo_id: null })
          .in("todo_id", todoIds);
        if (unlinkTodoPledgesError) {
          logger.error("[useBulkGoalActions] unlink todo pledges error:", unlinkTodoPledgesError);
          throw unlinkTodoPledgesError;
        }
      }

      // 4. Best-effort cleanup of community-goal participation (non-blocking).
      if (commonGoalIds.length > 0) {
        const { error: participationError } = await supabase
          .from("common_goal_participants")
          .delete()
          .eq("user_id", user.id)
          .in("common_goal_id", commonGoalIds);
        if (participationError) {
          logger.warn(
            "[useBulkGoalActions] bulkDelete participation cleanup failed (non-blocking):",
            participationError
          );
        }
      }

      // 5. Delete all selected goals in a single statement.
      const { error: deleteError, count } = await supabase
        .from("goals")
        .delete({ count: "exact" })
        .in("id", safeGoalIds)
        .eq("user_id", user.id);
      if (deleteError) {
        logger.error("[useBulkGoalActions] bulkDelete delete error:", deleteError);
        throw deleteError;
      }

      await invalidateGoals();
      return count ?? safeGoalIds.length;
    },
    [user?.id, invalidateGoals]
  );

  return { archiveGoals, restoreGoals, bulkDeleteGoals };
};
